import GridLayout from 'react-grid-layout';

import {
  compact,
  sortLayoutItemsByRowCol,
  // @ts-ignore
} from 'react-grid-layout/build/utils';

import {
  CANVAS_ITEM_MARGIN,
  CANVAS_PADDING_HORIZONTAL,
} from './canvasConstants';

export type LayoutTier = 'full' | 'tablet' | 'phone';

const GRID_COLS = 12;

// CanvasCard's fixed header (topBar) height — added on top of the measured grid
// content when refitting a card to its content in the scheduled-report PDF.
export const CARD_HEADER_PX = 40;
const REFIT_SAFETY_PX = 4;
const MIN_REFIT_H = 2;

// Target plot aspect ratio (width : height) used to cap chart cards in the PDF.
// A chart fills its card (height:100%), so a chart authored into a tall slot
// renders as a stretched plot — especially next to a grid that has refit down to
// its content. We cap over-tall chart cards to a natural height for their width
// so the plot keeps sensible proportions; shorter charts are left untouched.
const CHART_ASPECT_W_OVER_H = 1.8;

// Pixel width a card of grid-width `w` occupies at the given canvas width,
// accounting for the horizontal padding + inter-column margins.
const cardPixelWidth = ({
  w,
  canvasWidth,
}: {
  w: number;
  canvasWidth: number;
}): number => {
  const usable = canvasWidth - 2 * CANVAS_PADDING_HORIZONTAL;
  const colWidth = (usable - (GRID_COLS - 1) * CANVAS_ITEM_MARGIN) / GRID_COLS;
  return w * colWidth + (w - 1) * CANVAS_ITEM_MARGIN;
};

// Scheduled-report PDF only: rebuild the canvas layout so each grid card whose
// natural content height has been measured (contentHeights, keyed by layout.i)
// is resized to fit that content, chart cards are capped to a natural height for
// their width, then compact everything vertically to reclaim the freed
// whitespace. A react-grid-layout item of height `h` occupies
// `rowHeight*h + (h-1)*margin` px, so we invert that to find the `h` that fits.
// Non-editing items are `static:true` (which compact ignores), so we clear it
// here on the throwaway print layout.
export const buildHeadlessRefitLayout = ({
  layout,
  contentHeights,
  canvasRowHeight,
  canvasWidth,
  chartCapCardIds,
}: {
  layout: GridLayout.Layout[];
  contentHeights: Record<string, number>;
  canvasRowHeight: number;
  canvasWidth: number;
  // Ids of chart-like cards (no measured content height) eligible for the
  // aspect-ratio cap. Text/image/metric-list/etc. are excluded so their authored
  // height is preserved.
  chartCapCardIds: Set<string>;
}): GridLayout.Layout[] => {
  const denominator = canvasRowHeight + CANVAS_ITEM_MARGIN;

  const adjusted = layout.map((l) => {
    if (denominator <= 0) {
      return { ...l, static: false };
    }

    const contentPx = contentHeights[l.i];
    if (contentPx !== undefined) {
      // Measured grid/matrix — refit to its true content height.
      const neededPx = CARD_HEADER_PX + contentPx + REFIT_SAFETY_PX;
      const h = Math.max(
        MIN_REFIT_H,
        Math.ceil((neededPx + CANVAS_ITEM_MARGIN) / denominator),
      );
      return { ...l, h, static: false };
    }

    // Chart-like card: cap (never grow) to a natural height for its width so it
    // isn't stretched to fill a tall authored slot. Shorter charts keep their
    // authored height.
    if (chartCapCardIds.has(String(l.i)) && canvasWidth > 0) {
      const naturalPx =
        CARD_HEADER_PX +
        cardPixelWidth({ w: l.w, canvasWidth }) / CHART_ASPECT_W_OVER_H;
      const naturalH = Math.max(
        MIN_REFIT_H,
        Math.ceil((naturalPx + CANVAS_ITEM_MARGIN) / denominator),
      );
      return { ...l, h: Math.min(l.h, naturalH), static: false };
    }

    return { ...l, static: false };
  });

  return compact(
    sortLayoutItemsByRowCol(adjusted),
    'vertical',
    GRID_COLS,
  ) as GridLayout.Layout[];
};

// Canvas widths (px) below which the authored 12-column layout no longer fits
// comfortably and cards are reflowed instead. Measured on the canvas itself, so
// an open sidebar or side panel counts.
const FULL_LAYOUT_MIN_WIDTH = 800;
const TABLET_LAYOUT_MIN_WIDTH = 560;
// Reflowed cards keep their authored height, but never shrink below this.
const MIN_REFLOW_H = 3;
// Reflowed canvases scroll, so their rows needn't squeeze into a short (e.g.
// landscape phone) viewport.
export const MIN_REFLOW_ROW_HEIGHT = 18;
// A "tile" (KPI / Number-sized card) is small and short. On phones, tiles pair
// up two to a line rather than each taking the full width.
const PHONE_TILE_MAX_W = 3;
const PHONE_TILE_MAX_H = 6;

export const getLayoutTier = ({
  canvasWidth,
  canvasMode,
}: {
  canvasWidth: number;
  canvasMode?: CanvasMode;
}): LayoutTier => {
  if (canvasMode === 'desktop') {
    return 'full';
  }
  if (canvasMode === 'mobile') {
    return 'phone';
  }
  if (canvasWidth >= FULL_LAYOUT_MIN_WIDTH) {
    return 'full';
  }
  if (canvasWidth >= TABLET_LAYOUT_MIN_WIDTH) {
    return 'tablet';
  }
  return 'phone';
};

const reflowWidth = ({
  layout,
  tier,
}: {
  layout: GridLayout.Layout;
  tier: LayoutTier;
}): number => {
  const { w, h } = layout;
  if (tier === 'tablet') {
    if (w <= 2) {
      return 4;
    }
    if (w <= 4) {
      return 6;
    }
    return GRID_COLS;
  }
  if (w <= PHONE_TILE_MAX_W && h <= PHONE_TILE_MAX_H) {
    return 6;
  }
  return GRID_COLS;
};

// Reflows an authored 12-column layout for a narrow canvas. Cards are taken in
// reading order (row, then column), widened for the tier and packed left to
// right, wrapping onto a new line when the next card won't fit. Each line's
// last card stretches to the right edge and every card in a line takes the
// line's tallest height, so lines have clean edges and no gaps. Cards keep
// their authored height — a KPI strip stays a strip of short tiles.
export const reflowLayout = ({
  layout,
  tier,
}: {
  layout: GridLayout.Layout[];
  tier: LayoutTier;
}): GridLayout.Layout[] => {
  if (tier === 'full') {
    return layout;
  }

  const ordered = sortLayoutItemsByRowCol(layout) as GridLayout.Layout[];
  const result: GridLayout.Layout[] = [];
  let line: GridLayout.Layout[] = [];
  let cursorX = 0;
  let lineY = 0;

  const closeLine = () => {
    if (line.length === 0) {
      return;
    }
    const lineH = Math.max(...line.map((l) => l.h));
    const last = line[line.length - 1];
    line[line.length - 1] = { ...last, w: GRID_COLS - last.x };
    result.push(...line.map((l) => ({ ...l, h: lineH })));
    lineY += lineH;
    line = [];
    cursorX = 0;
  };

  ordered.forEach((l) => {
    const w = reflowWidth({ layout: l, tier });
    if (cursorX + w > GRID_COLS) {
      closeLine();
    }
    line.push({
      ...l,
      x: cursorX,
      y: lineY,
      w,
      h: Math.max(MIN_REFLOW_H, l.h),
    });
    cursorX += w;
  });
  closeLine();

  return result;
};

const buildLayout = ({
  cards,
  isEditing,
  tier,
}: {
  cards: CanvasCard.Card[];
  isEditing: boolean;
  tier: LayoutTier;
}): GridLayout.Layout[] => {
  const baseLayout = cards.map((c) => ({
    ...c.layout,
    isDraggable: undefined,
    isResizable: undefined,
    static: isEditing ? false : true,
  }));

  // Edits are made to (and saved from) the authored layout, never a reflow.
  return isEditing ? baseLayout : reflowLayout({ layout: baseLayout, tier });
};

export default buildLayout;
